import React, {useState, useEffect} from "react";
import {Link} from "react-router";
import _ from "lodash";
import {
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
} from "@material-ui/core";
import ArchiveIcon from "@material-ui/icons/Archive";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import VisibilityIcon from "@material-ui/icons/Visibility";
import InfoIcon from "@material-ui/icons/Info";
import LinkIcon from "@material-ui/icons/Link";

import Expando from "routes/workflow/workflow_detail/components/expando";
import ConfigurationInputs, {DataAssetReference} from "./configuration_inputs";
import ConfigurationSelector from "common_components/flowmaster/data_asset/configuration_selector";
import ConfigurationDuplicateDialog from "./dialogs/configuration_duplicate_dialog";
import ConfigurationEditNamesDialog from "./dialogs/configuration_edit_names_dialog";
import ConfigurationCreateDialog from "./dialogs/configuration_create_dialog";

import {OrganisationId} from "common/types/organisation";
import {Definition, DefinitionConfig} from "common/flowmaster/types/data_asset";

import {
  WorkflowInputValues,
  WorkflowInputValue,
  WorkflowInstanceConfig,
  WorkflowInstance,
} from "common/flowmaster/types/workflow";
import {Uuid} from "common/types";
import SplitSaveButton from "routes/data_assets/util/split_save_button";
import {isDataAsset} from "../containers/data_assets_create_view";

const generateDefaultAssetName = (
  definitionName?: string,
  configName?: string,
) => {
  if (!definitionName || !configName) {
    return "";
  }
  return `${definitionName} - ${configName}`;
};

const styles = {
  sectionLabel: {
    display: "flex",
    alignItems: "center",
    height: "48px",
    fontWeight: "bold",
  },
  errorText: {
    color: "#f44336",
    fontSize: "0.75rem",
  },
  helperText: {
    color: "#0000008A",
    fontSize: "0.75rem",
    marginTop: "0.5em",
  },
  container: {
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "1em",
    display: "flex",
    flexDirection: "column",
    gap: "1em",
    marginBottom: "1em",
  },
  configRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "1em",
    marginTop: "1em",
    justifyContent: "space-between",
  },
  configControls: {
    display: "flex",
    alignItems: "flex-start",
    gap: "1em",
    flex: 1,
    flexWrap: "wrap",
  },
  configSelectorContainer: {
    display: "flex",
    alignItems: "flex-end",
    gap: "0.5em",
  },
  configNameField: {
    width: "24em",
  },
  defaultAssetNameField: {
    width: "20em",
    alignSelf: "center",
    height: 56,
  },
  button: {
    height: 56,
    fontSize: "1.1rem",
    borderRadius: "4px",
    minWidth: 120,
  },
  archiveButton: {
    width: 48,
    height: 48,
    padding: 0,
  },
  archiveIcon: {
    fontSize: 48,
  },
  rightControls: {
    display: "flex",
    alignItems: "flex-start",
    gap: "1em",
    flexWrap: "wrap",
    justifyContent: "end",
  },
} as const;

interface ConfigurationSectionProps {
  selectedDefinition?: Definition;
  selectedConfigId: number | string;
  definitions: Definition[];
  configurations: DefinitionConfig[];
  configExpanded: boolean;
  basePath: string;
  push: (path: string) => void;
  handleConfigChange: (configId: number) => void;
  updateAllConfigNames: (name: string) => void;
  createDefinitionConfig: (
    config: WorkflowInstanceConfig,
    name: string,
    groupId?: Uuid,
    archive?: boolean,
  ) => Promise<void>;
  setConfigHasChanges: (hasChanges: boolean) => void;
  updateConfigIsArchived: (isArchived: boolean) => void;
  updateConfigIsPublic: (isPrivate: boolean) => void;
  updateConfigDefaultAssetName: (defaultAssetName: string) => void;
  organisationId: OrganisationId;
  isLatestDefinition: boolean;
  latestDefinition: Definition;
  latestConfig: DefinitionConfig;
}

const ConfigurationSection: React.FC<ConfigurationSectionProps> = ({
  selectedDefinition,
  selectedConfigId,
  definitions,
  configurations,
  configExpanded,
  basePath,
  push,
  handleConfigChange,
  createDefinitionConfig,
  updateAllConfigNames,
  setConfigHasChanges,
  updateConfigIsArchived,
  updateConfigIsPublic,
  updateConfigDefaultAssetName,
  organisationId,
  isLatestDefinition,
  latestDefinition,
  latestConfig,
}) => {
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showNameUpdateDialog, setShowNameUpdateDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [inputValues, setInputValues] = useState<WorkflowInputValues>({});
  const [
    initialInputValues,
    setInitialInputValues,
  ] = useState<WorkflowInputValues>({});
  const [draftMode, setDraftMode] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [previousConfigId, setPreviousConfigId] = useState<number | string>("");
  const [defaultAssetName, setDefaultAssetName] = useState("");
  const selectedConfig = draftMode
    ? null
    : configurations.find(config => config.id === Number(selectedConfigId));

  const configName = draftMode ? draftName : selectedConfig?.name;

  const definitionHasInputs = Boolean(
    selectedDefinition?.inputs?.values?.length,
  );
  const definitionIsArchived = selectedDefinition?.is_archived;
  const canEditConfiguration =
    (Boolean(selectedConfig) || draftMode) && !definitionIsArchived;

  const inputsChanged = !_.isEqual(inputValues, initialInputValues);

  const configNameGroupIds = new Set(
    configurations
      .filter(({name}) => name === configName)
      .map(({group_id}) => group_id),
  );

  const conflictingName =
    configNameGroupIds.size > 1 ||
    (configNameGroupIds.size === 1 &&
      Array.from(configNameGroupIds)[0] !== selectedConfig?.group_id);

  const isNameValid = configName?.trim() !== "" && !conflictingName;

  const hasRequiredInputValues = () => {
    if (!selectedDefinition?.inputs?.values) {
      return true;
    }

    return selectedDefinition.inputs.values.every(input => {
      const inputId = String(input.id);
      const inputValue = inputValues[inputId];

      if (isDataAsset(inputValue)) {
        return dataAssetInputHasRequiredValues(inputValue);
      }

      if (input.has_default && input.default_value !== undefined) {
        return true;
      }

      if (input.is_optional) {
        return true;
      }

      return inputValue !== undefined && inputValue !== "";
    });
  };

  const getOriginalDefaultAssetName = () => {
    if (!selectedConfig) {
      return "";
    }
    return (
      selectedConfig.config?.default_asset_name ||
      generateDefaultAssetName(selectedDefinition?.name, selectedConfig.name)
    );
  };

  const defaultAssetNameChanged =
    !draftMode && defaultAssetName !== getOriginalDefaultAssetName();
  const hasChanges = draftMode
    ? true
    : inputsChanged || defaultAssetNameChanged;

  const canSave =
    hasChanges &&
    isNameValid &&
    hasRequiredInputValues() &&
    defaultAssetName.trim() !== "";

  const canSaveAndArchive = canSave && !draftMode && inputsChanged;

  const isDefaultAssetNameValid = defaultAssetName.trim() !== "";

  const showInputs =
    selectedDefinition && definitionHasInputs && (selectedConfig || draftMode);

  const resetInputValues = (values: WorkflowInputValues = {}) => {
    setInputValues({...values});
    setInitialInputValues({...values});
  };

  const resetConfiguration = (config?: DefinitionConfig | null) => {
    if (config?.config?.inputs?.values) {
      resetInputValues(config.config.inputs.values);
    } else {
      resetInputValues();
    }
  };

  useEffect(() => {
    if (!draftMode) {
      resetConfiguration(selectedConfig);

      if (selectedConfig) {
        const existingName = selectedConfig.config?.default_asset_name;
        const defaultName =
          existingName ||
          generateDefaultAssetName(
            selectedDefinition?.name,
            selectedConfig.name,
          );
        setDefaultAssetName(defaultName);
      } else {
        setDefaultAssetName("");
      }
    }
  }, [selectedConfig, selectedDefinition, draftMode]);

  useEffect(() => {
    setConfigHasChanges(hasChanges);
  }, [hasChanges, setConfigHasChanges]);

  const handleInputChange = (inputId: string, value: WorkflowInputValue) => {
    const input = selectedDefinition?.inputs?.values?.find(
      input => input.id === parseInt(inputId, 10),
    );

    if (input?.has_default && input.default_value === value) {
      setInputValues(prev => _.omit(prev, inputId));
      return;
    }

    setInputValues(prev => ({
      ...prev,
      [inputId]: value,
    }));
  };

  const handleDefaultAssetNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setDefaultAssetName(event.target.value);
  };

  const handleCreateDefinitionConfig = (
    name: string,
    groupId?: WorkflowInstance["group_id"],
    defaultAssetName?: string,
    archive?: boolean,
  ) => {
    const configData = {
      inputs: {values: inputValues},
      default_asset_name: defaultAssetName,
    };
    createDefinitionConfig(configData, name, groupId, archive);
    setDraftMode(false);
  };

  const handleArchiveConfig = () => {
    updateConfigIsArchived(true);
  };

  const handleDuplicateButton = () => {
    setShowDuplicateDialog(true);
  };

  const handleEditNameButton = () => {
    setShowNameUpdateDialog(true);
  };

  const closeEditAllDialog = () => setShowNameUpdateDialog(false);
  const closeDuplicateDialog = () => setShowDuplicateDialog(false);
  const closeCreateDialog = () => setShowCreateDialog(false);

  const handleCreateButton = () => {
    setShowCreateDialog(true);
  };

  const handleDuplicate = (configName: string) => {
    setShowDuplicateDialog(false);
    setPreviousConfigId(selectedConfigId);
    setDraftMode(true);
    setDraftName(configName);

    const existingDefaultAssetName = selectedConfig?.config?.default_asset_name;
    const defaultAssetName =
      existingDefaultAssetName ||
      generateDefaultAssetName(selectedDefinition?.name, configName);
    setDefaultAssetName(defaultAssetName);

    if (selectedConfig?.config?.inputs?.values) {
      resetInputValues(selectedConfig.config.inputs.values);
    }

    const queryParams = location.search;
    push(`${basePath}/definition/${selectedDefinition?.id}${queryParams}`);
  };

  const handleSave = (archive = false) => {
    if (configName) {
      const onlyDefaultAssetNameChanged =
        !draftMode && !inputsChanged && defaultAssetNameChanged;

      if (onlyDefaultAssetNameChanged) {
        updateConfigDefaultAssetName(defaultAssetName);
      } else {
        if (draftMode) {
          handleCreateDefinitionConfig(configName, undefined, defaultAssetName);
        } else {
          handleCreateDefinitionConfig(
            configName,
            selectedConfig?.group_id,
            defaultAssetName,
            archive,
          );
        }
      }
    }
  };

  const handleEditAllNames = (name: string) => {
    closeEditAllDialog();
    updateAllConfigNames(name);
  };

  const handleCreateNew = (name: string) => {
    setPreviousConfigId(selectedConfigId);

    setDraftMode(true);
    setDraftName(name);

    const defaultName = generateDefaultAssetName(
      selectedDefinition?.name,
      name,
    );
    setDefaultAssetName(defaultName);

    resetInputValues();

    const queryParams = location.search;
    push(`${basePath}/definition/${selectedDefinition?.id}${queryParams}`);
  };

  const handleCancelEdit = () => {
    resetConfiguration(selectedConfig);

    if (selectedConfig) {
      const existingName = selectedConfig.config?.default_asset_name;
      const defaultName =
        existingName ||
        generateDefaultAssetName(selectedDefinition?.name, selectedConfig.name);
      setDefaultAssetName(defaultName);
    }
  };

  const handleCancelDraft = () => {
    setDraftMode(false);
    setDraftName("");
    setDefaultAssetName("");

    if (previousConfigId) {
      const queryParams = location.search;
      push(
        `${basePath}/definition/${selectedDefinition?.id}/config/${previousConfigId}${queryParams}`,
      );
    } else {
      const queryParams = location.search;
      push(`${basePath}/definition/${selectedDefinition?.id}${queryParams}`);
    }
  };

  const handleSetPublicConfig = () => {
    updateConfigIsPublic(!selectedConfig?.is_public);
  };

  const renderHeader = () => (
    <div style={{display: "flex", alignItems: "center"}}>
      <Typography variant="body1" style={styles.sectionLabel}>
        Configuration
      </Typography>
    </div>
  );

  const renderLatestConfigurationText = (
    organisationId: OrganisationId,
    latestDefinition: Definition,
    latestConfig: DefinitionConfig,
  ) => (
    <Typography
      variant="body2"
      style={{
        color: "gray",
        display: "flex",
        alignItems: "center",
        gap: "0.5em",
      }}
    >
      <InfoIcon fontSize="small" />
      <span>
        There is a later definition with a matching config{" "}
        <Chip label={latestDefinition.name} />
      </span>
      <Link
        to={`/organisation/${organisationId}/data-assets/definition/${latestDefinition.id}/config/${latestConfig.id}`}
      >
        <IconButton
          size="small"
          style={{padding: 0}}
          aria-label="link to latest config"
        >
          <LinkIcon />
        </IconButton>
      </Link>
    </Typography>
  );

  const renderHelperText = () => {
    if (draftMode) {
      return (
        <Typography variant="body2" style={styles.helperText}>
          Complete the configuration and click Save to create it.
        </Typography>
      );
    }

    if (!selectedDefinition) {
      return (
        <Typography variant="body2" style={styles.helperText}>
          Please select a definition first.
        </Typography>
      );
    }

    if (configurations.length === 0 && !draftMode) {
      return (
        <Typography variant="body2" style={styles.helperText}>
          No configurations available for this definition.
        </Typography>
      );
    }

    return null;
  };
  const configContent = (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1.5em",
      }}
    >
      {renderHeader()}
      {selectedConfig &&
        !isLatestDefinition &&
        renderLatestConfigurationText(
          organisationId,
          latestDefinition,
          latestConfig,
        )}
      <>
        {selectedConfigId && showNameUpdateDialog && (
          <ConfigurationEditNamesDialog
            open={showNameUpdateDialog}
            initialName={configName ?? ""}
            onClose={closeEditAllDialog}
            onRenameAll={handleEditAllNames}
          />
        )}
        {selectedConfigId && showDuplicateDialog && (
          <ConfigurationDuplicateDialog
            initialName={configName ?? ""}
            open={showDuplicateDialog}
            onClose={closeDuplicateDialog}
            onDuplicate={handleDuplicate}
          />
        )}
        {showCreateDialog && (
          <ConfigurationCreateDialog
            open={showCreateDialog}
            onClose={closeCreateDialog}
            onCreate={handleCreateNew}
          />
        )}

        <div style={styles.configRow}>
          <div style={styles.configControls}>
            <div style={styles.configSelectorContainer}>
              <ConfigurationSelector
                selectedConfigId={selectedConfigId}
                configurations={configurations}
                selectedDefinition={selectedDefinition}
                handleConfigChange={handleConfigChange}
                handleCreateNew={handleCreateButton}
                formControlStyles={{
                  group: {
                    width: "25em",
                    flexShrink: 0,
                  },
                  version: {
                    width: "14em",
                    flexShrink: 0,
                  },
                }}
                draftMode={draftMode}
                draftName={draftName}
                disabled={definitionIsArchived}
                canCreateNew={true}
              />
            </div>

            {canEditConfiguration && (
              <>
                <SplitSaveButton
                  canSave={canSave}
                  canSaveAndArchive={canSaveAndArchive}
                  isDraftMode={draftMode}
                  styles={styles}
                  handleSave={handleSave}
                  handleSaveAndArchive={() => handleSave(true)}
                />

                {(draftMode || hasChanges) && (
                  <Button
                    style={styles.button}
                    variant="outlined"
                    onClick={draftMode ? handleCancelDraft : handleCancelEdit}
                  >
                    Cancel
                  </Button>
                )}
                {!draftMode && (
                  <>
                    <Button
                      style={styles.button}
                      variant="contained"
                      color="primary"
                      onClick={handleEditNameButton}
                      disabled={definitionIsArchived}
                    >
                      Edit Name
                    </Button>
                    <Button
                      style={styles.button}
                      variant="contained"
                      color="primary"
                      onClick={handleDuplicateButton}
                      disabled={definitionIsArchived}
                    >
                      Duplicate
                    </Button>
                  </>
                )}
              </>
            )}
          </div>

          <div style={styles.rightControls}>
            {canEditConfiguration && (
              <TextField
                label="Default asset name"
                value={defaultAssetName}
                onChange={handleDefaultAssetNameChange}
                variant="outlined"
                style={styles.defaultAssetNameField}
                placeholder="Enter default asset name"
                error={!isDefaultAssetNameValid}
                disabled={definitionIsArchived}
              />
            )}

            {selectedConfig && !selectedConfig.is_archived && !draftMode && (
              <>
                <IconButton
                  color="secondary"
                  style={styles.archiveButton}
                  onClick={handleArchiveConfig}
                  title="Archive configuration"
                  disabled={definitionIsArchived}
                >
                  <ArchiveIcon style={styles.archiveIcon} />
                </IconButton>
                <IconButton
                  style={styles.archiveButton}
                  onClick={handleSetPublicConfig}
                  title={
                    selectedConfig.is_public
                      ? "Make configuration private"
                      : "Make configuration public"
                  }
                  disabled={definitionIsArchived}
                >
                  {selectedConfig.is_public ? (
                    <VisibilityIcon style={styles.archiveIcon} />
                  ) : (
                    <VisibilityOffIcon style={styles.archiveIcon} />
                  )}
                </IconButton>
              </>
            )}
          </div>
        </div>

        {renderHelperText()}

        {showInputs && (
          <ConfigurationInputs
            key={draftMode ? `draft-${draftName}` : selectedConfigId}
            definition={selectedDefinition}
            inputValues={inputValues}
            initialInputValues={initialInputValues}
            onInputChange={handleInputChange}
            definitions={definitions}
            organisationId={organisationId}
            disabled={selectedConfig?.is_archived || definitionIsArchived}
          />
        )}
      </>
    </div>
  );

  return (
    <div style={styles.container}>
      <Expando beginExpanded={configExpanded}>
        {renderHeader()}
        {configContent}
      </Expando>
    </div>
  );
};

function dataAssetInputHasRequiredValues(input: DataAssetReference): boolean {
  if (input.definition_id === "" && !input.definition_group_id) {
    return false;
  }

  if (input.config_id === "" && !input.config_group_id) {
    return false;
  }

  return true;
}

export default ConfigurationSection;
